import { request } from './requests';

type GrantRole = {
    name?: string;
};

type OrganizationGrant = {
    entity_type?: string;
    role?: GrantRole;
};

type GrantsResponse = {
    grants?: OrganizationGrant[];
    // Some fetchers use `objects`; tolerate it just in case.
    objects?: OrganizationGrant[];
};

const getGrantsUrlForUser = (organizationId: string, userId: string) =>
    `/api/v3/organizations/${organizationId}/grants/?expand.organization_grant=role&user_ids=${userId}`;

const normalizeRoleName = (name: string) => name.trim().toLowerCase();

/**
 * Returns org membership role label like "owner", "admin", etc.
 *
 * If multiple distinct roles are found, returns "multiple roles".
 */
export async function getOrganizationRoleSummary(
    organizationId: string,
    userId: string,
): Promise<string | null> {
    if (!organizationId || !userId) return null;

    const response = await request<GrantsResponse>(
        'GET',
        getGrantsUrlForUser(organizationId, userId),
    );

    const grants = response?.grants || response?.objects || [];

    // Prefer org-wide grants (entity_type === 'user') when present.
    const roleNamesFromUserGrants = grants
        .filter((g) => g.entity_type === 'user')
        .map((g) => g.role?.name)
        .filter((name): name is string => Boolean(name));

    const roleNames = (
        roleNamesFromUserGrants.length
            ? roleNamesFromUserGrants
            : grants
                  .map((g) => g.role?.name)
                  .filter((name): name is string => Boolean(name))
    ).map(normalizeRoleName);

    const uniqueRoleNames = Array.from(new Set(roleNames));
    if (uniqueRoleNames.length === 0) return null;
    if (uniqueRoleNames.length === 1) return uniqueRoleNames[0];
    return 'multiple roles';
}
